import { useCallback, useEffect, useRef, useState } from 'react';
import practiceClient from '../client/PracticeClient';

/**
 * Сессия практики: окно вопросов держится локально, ответы копятся и уходят пачкой.
 *
 * До этого каждая из трёх страниц прохождения (карточки, квизы, слова) сама ходила на
 * `POST /api/practice` за каждым вопросом и почти дословно повторяла две соседние.
 *
 * Порог сброса — настройка, а не константа: `flushEvery` задаёт, через сколько ответов
 * буфер уходит на сервер. Ноль означает «только при исчерпании окна». Независимо от
 * порога буфер сбрасывается при исчерпании окна и при уходе со страницы — иначе
 * значение «0» теряло бы последнее окно целиком.
 */
export const DEFAULT_FLUSH_EVERY = 5;

/** Отказы не про саму пачку, а про этот момент: «слишком долго», «рано», «слишком часто». */
const RETRYABLE_STATUSES = new Set([408, 425, 429]);

/**
 * Стоит ли повторять неудачную отправку ответов.
 *
 * Повтор безопасен — сервер опознаёт уже записанный ответ по идентификатору события и
 * по времени и не считает его второй раз, — но безопасный ещё не значит осмысленный. Отсутствие кода означает, что
 * ответа не было вовсе: запрос мог и дойти, и не дойти, и такую пачку надо прислать
 * снова. Сервер, который ответил отказом, запрос уже разобрал: 400 на пачке без
 * методики или 404 на удалённой колоде повторятся слово в слово сколько угодно раз.
 */
const worthRetrying = (error) => {
    const status = error?.status;
    return status === undefined
        || status >= 500
        || RETRYABLE_STATUSES.has(status);
};

/**
 * Идентификатор события — второй ключ повтора рядом с временем ответа. Время сервер
 * может заменить своим (часы устройства, убежавшие вперёд), и тогда повтор пачки по
 * времени не опознаётся; идентификатор выдаётся один раз и повторяется слово в слово.
 */
const newEventId = () => {
    if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const usePracticeSession = ({ practiceId, learningMethod, flushEvery = DEFAULT_FLUSH_EVERY }) => {
    const [items, setItems] = useState([]);
    const [index, setIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [finished, setFinished] = useState(false);
    const [error, setError] = useState(null);

    // В ref, а не в состоянии: буфер читается из обработчиков и из размонтирования,
    // и лишняя перерисовка на каждый ответ здесь ни к чему.
    const pendingEvents = useRef([]);
    // Когда показали текущий вопрос. По нему считается время раздумья: восстановить его
    // потом неоткуда, а быстрый верный ответ — более сильное свидетельство, чем долгий.
    const shownAt = useRef(Date.now());
    const sessionRef = useRef({ practiceId, learningMethod });

    useEffect(() => {
        sessionRef.current = { practiceId, learningMethod };
    }, [practiceId, learningMethod]);

    const flush = useCallback(async ({ onLeave = false } = {}) => {
        const events = pendingEvents.current;
        if (events.length === 0) {
            return;
        }
        // Буфер очищаем до отправки: иначе ответ, данный во время запроса, попал бы
        // в ту же пачку второй раз.
        pendingEvents.current = [];
        const { practiceId: id, learningMethod: method } = sessionRef.current;
        const batch = { practiceId: id, learningMethod: method, events };
        try {
            await (onLeave
                ? practiceClient.submitEventsOnLeave(batch)
                : practiceClient.submitEvents(batch));
        } catch (err) {
            console.error('Не удалось отправить ответы практики:', err);
            if (!worthRetrying(err)) {
                // Отказ, который не пройдёт и со второй попытки. Вернуть такую пачку в
                // буфер значит возить её до конца занятия: каждый следующий сброс шлёт
                // её снова вместе с новыми ответами, снова получает отказ, и пачка
                // только растёт. Эти ответы потеряны — но потеряны они уже здесь.
                return;
            }
            // Ответы возвращаются в буфер и уйдут со следующей пачкой. Повторять можно,
            // не разбирая, дошёл ли запрос: идентификатор события и пара «элемент и
            // время ответа» в журнале уникальны, и повтор уже записанного сервер
            // применяет ноль раз. Впереди
            // новых, потому что они и случились раньше; порядок внутри пачки сервер
            // всё равно восстанавливает по времени ответа.
            pendingEvents.current = [...events, ...pendingEvents.current];
        }
    }, []);

    const loadWindow = useCallback(async () => {
        setIsLoading(true);
        try {
            const { practiceId: id, learningMethod: method } = sessionRef.current;
            const response = await practiceClient.getWindow({ practiceId: id, learningMethod: method });
            // Вопросы, ответы на которые ещё не дошли до сервера, он в расчёт не принял:
            // окно приходит то же самое. Показать их второй раз значит попросить ответить
            // ещё раз — и оба ответа будут настоящими, с разным временем, так что ключ
            // журнала их не склеит и одно занятие зачтётся дважды. Уникальный ключ
            // защищает от повтора пачки, а не от повторного прохождения окна.
            const undelivered = new Set(pendingEvents.current.map((event) => event.itemId));
            const fresh = (response.items || []).filter((item) => !undelivered.has(item.itemId));
            setItems(fresh);
            setIndex(0);
            if (fresh.length === 0 && undelivered.size > 0) {
                // Продолжать нечего: всё, что сервер прислал, уже отвечено и ждёт
                // отправки. Занятие не «закончилось» — оно упёрлось в недоставленные
                // ответы, и говорить об этом надо прямо. Уходя со страницы, клиент
                // дошлёт их запросом, переживающим закрытие вкладки.
                setError('Your answers have not reached the server yet. Please try again in a moment.');
                return;
            }
            // Конец практики приходит признаком, а не текстом ошибки: сравнение
            // формулировки ломалось бы от любой правки сообщения на бэкенде.
            setFinished(Boolean(response.exhausted) || fresh.length === 0);
        } catch (err) {
            console.error('Не удалось получить вопросы практики:', err);
            setError(err.message || 'Failed to load practice questions.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!practiceId) {
            setError('Practice ID is missing.');
            setIsLoading(false);
            return;
        }
        loadWindow();
    }, [practiceId, learningMethod, loadWindow]);

    // Уход со страницы и закрытие вкладки: несохранённые ответы теряться не должны.
    useEffect(() => {
        const onUnload = () => {
            if (pendingEvents.current.length > 0) {
                flush({ onLeave: true });
            }
        };
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
            onUnload();
        };
    }, [flush]);

    const answer = useCallback(async (correct, evidence = {}) => {
        const current = items[index];
        if (!current) {
            return;
        }
        pendingEvents.current.push({
            eventId: newEventId(),
            itemId: current.itemId,
            correct,
            answeredAt: new Date().toISOString(),
            // Подробности ответа: сколько попыток понадобилось, из скольких вариантов
            // выбирали и сколько думали. Сервер их не выведет, а вес формата и поправка
            // на угадывание считаются именно по ним.
            // Пусто значит «неизвестно», а не «одна попытка»: карточка, которая попыток
            // не считает, не должна утверждать, что их была одна — журнал не переписывается.
            attempts: evidence.attempts ?? null,
            optionCount: evidence.optionCount ?? null,
            // Время раздумья карточка может измерить точнее: она знает момент выбора, а
            // здесь ответ приходит уже после показа правильного варианта и анимации.
            responseMs: evidence.responseMs ?? Math.max(0, Date.now() - shownAt.current),
        });
        shownAt.current = Date.now();

        const isLastInWindow = index + 1 >= items.length;
        if (isLastInWindow) {
            await flush();
            await loadWindow();
            return;
        }

        if (flushEvery > 0 && pendingEvents.current.length >= flushEvery) {
            await flush();
        }
        setIndex((value) => value + 1);
    }, [items, index, flush, loadWindow, flushEvery]);

    // Новый вопрос на экране — секундомер с нуля. Иначе время первого ответа в окне
    // включало бы загрузку окна, а не раздумье.
    useEffect(() => {
        shownAt.current = Date.now();
    }, [index, items]);

    return {
        item: items[index] || null,
        isLoading,
        finished,
        error,
        answer,
    };
};

export default usePracticeSession;
